/**
 * /admin/spend — AWS Cost Explorer rollup for the ZCD account.
 *
 * Replaces the standalone zen-cost-monitor Flask dashboard for daily
 * "how much did we burn today" checks. Numbers are MTD by service +
 * previous-month total + daily for the last 7 days.
 *
 * Source: GET /api/admin/v2/finances/aws-spend/ (10-min server-side
 * cache; CE charges $0.01 per request).
 *
 * Caveats surfaced inline on the page:
 * - Yesterday's daily figure typically lags ~24h (CE backfills).
 * - ZenSportsLive lives in a separate AWS account and is NOT included.
 */
import { useEffect, useMemo, useState } from "react";
import { RefreshCw, AlertTriangle, Loader2, Cloud } from "lucide-react";
import { getAwsSpend } from "../../api/owner.api";

function fmtUsd(amount) {
  if (amount == null) return "—";
  return `$${Number(amount).toFixed(2)}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso + "T00:00:00Z").toLocaleDateString("en-IN", {
    day: "2-digit", month: "short",
  });
}

export default function AdminSpend() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await getAwsSpend();
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }

  const dailyMax = useMemo(() => {
    if (!data?.daily_last_7d?.length) return 1;
    return Math.max(...data.daily_last_7d.map((d) => d.amount_usd)) || 1;
  }, [data]);

  return (
    <div className="p-6 space-y-4">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Cloud className="w-5 h-5" /> AWS Spend
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            ZCD account (890989339178). ZenSportsLive runs in a separate AWS
            account and is not included here.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data?.cache_age_sec != null && (
            <span className="text-[11px] text-text-secondary">
              cache age: {data.cache_age_sec}s
            </span>
          )}
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border border-border-default hover:bg-bg-secondary disabled:opacity-50"
          >
            {loading
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <RefreshCw className="w-4 h-4" />}
            Refresh
          </button>
        </div>
      </header>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-md border border-red-500/40 bg-red-500/10 text-sm">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-red-500" />
          <div>{error}</div>
        </div>
      )}

      {loading && !data && (
        <div className="flex items-center gap-2 text-text-secondary text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      )}

      {data && (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <KpiCard
              label="Month-to-date"
              value={fmtUsd(data.mtd_total_usd)}
              sub={`since ${fmtDate(data.month_start)}`}
            />
            <KpiCard
              label="Previous month"
              value={fmtUsd(data.previous_month_total_usd)}
            />
            <KpiCard
              label="MTD vs prev month"
              value={
                data.previous_month_total_usd
                  ? `${Math.round(
                      (data.mtd_total_usd / data.previous_month_total_usd) * 100,
                    )}%`
                  : "—"
              }
              sub="run-rate share so far"
            />
          </div>

          {/* By service */}
          <section className="rounded-lg border border-border-default bg-bg-primary overflow-hidden">
            <header className="px-4 py-2.5 border-b border-border-default text-sm font-medium">
              MTD by service
            </header>
            {data.mtd_by_service?.length ? (
              <table className="w-full text-sm">
                <thead className="text-text-secondary text-[12px] bg-bg-secondary/40">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Service</th>
                    <th className="text-right px-4 py-2 font-medium w-32">USD</th>
                    <th className="text-right px-4 py-2 font-medium w-24">% MTD</th>
                  </tr>
                </thead>
                <tbody>
                  {data.mtd_by_service.map((row) => {
                    const pct = data.mtd_total_usd
                      ? (row.amount_usd / data.mtd_total_usd) * 100
                      : 0;
                    return (
                      <tr key={row.service} className="border-t border-border-default">
                        <td className="px-4 py-2">{row.service}</td>
                        <td className="px-4 py-2 text-right tabular-nums">
                          {fmtUsd(row.amount_usd)}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums text-text-secondary">
                          {pct.toFixed(1)}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="px-4 py-6 text-sm text-text-secondary text-center">
                No spend recorded this month yet.
              </div>
            )}
          </section>

          {/* Daily 7d */}
          <section className="rounded-lg border border-border-default bg-bg-primary overflow-hidden">
            <header className="px-4 py-2.5 border-b border-border-default text-sm font-medium">
              Last 7 days (daily)
              <span className="ml-2 text-[11px] text-text-secondary font-normal">
                most recent day usually lags ~24h while CE backfills
              </span>
            </header>
            <div className="p-3 space-y-1.5">
              {data.daily_last_7d?.length
                ? data.daily_last_7d.map((row) => (
                    <div key={row.date} className="flex items-center gap-3 text-sm">
                      <div className="w-16 text-text-secondary tabular-nums">
                        {fmtDate(row.date)}
                      </div>
                      <div className="flex-1 h-2.5 bg-bg-secondary rounded">
                        <div
                          className="h-2.5 rounded bg-button-primary"
                          style={{
                            width: `${(row.amount_usd / dailyMax) * 100}%`,
                          }}
                        />
                      </div>
                      <div className="w-20 text-right tabular-nums">
                        {fmtUsd(row.amount_usd)}
                      </div>
                    </div>
                  ))
                : (
                  <div className="text-sm text-text-secondary px-1 py-3">
                    No daily data.
                  </div>
                )}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function KpiCard({ label, value, sub }) {
  return (
    <div className="rounded-lg border border-border-default bg-bg-primary p-4">
      <div className="text-[11px] uppercase tracking-wider text-text-secondary">
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
      {sub && <div className="mt-0.5 text-[11px] text-text-secondary">{sub}</div>}
    </div>
  );
}
